import type { PostPayBalanceResponse } from '@/store/api/webPaymentsCompat';

export type ParsedPaymentResponse =
  | {
      approved: true;
      // Server-authoritative charged amount. `undefined` when the response
      // omitted it or the value couldn't be parsed — callers should fall
      // back to their client-side computed total in that case.
      amount: number | undefined;
      paymentId?: number;
      retref?: string;
      authcode?: string;
    }
  | {
      approved: false;
      errorMessage: string;
    };

/**
 * Single source of truth for interpreting a POST /api/v3/payments response.
 *
 * Handles the case where RTK Query's `isSuccess` (HTTP 2xx) is true but the
 * envelope or processor disagree — e.g. `{ success: false }` or a decline
 * `respstat`. Falls through to trusting `result.success` when `respstat` is
 * absent so legacy SOAP-path responses still resolve correctly.
 */
export function parsePaymentResponse(
  result: PostPayBalanceResponse | undefined,
): ParsedPaymentResponse {
  const processor = result?.response;
  const approved = processor?.respstat ? processor.respstat === 'A' : true;

  if (!result?.success || !approved) {
    return {
      approved: false,
      errorMessage: processor?.resptext || 'Payment was not approved.',
    };
  }

  const rawAmount = processor?.amount;
  const parsedAmount = rawAmount != null ? Number(rawAmount) : NaN;

  return {
    approved: true,
    amount: Number.isFinite(parsedAmount) ? parsedAmount : undefined,
    paymentId: processor?.paymentId,
    retref: processor?.retref,
    authcode: processor?.authcode,
  };
}
